import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Ensure the Firestore instance is imported
import './BIMProject.css';

const BIMProject = () => {
  const [futiristicProjects, setFutiristicProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  
  // State for adding a new project
  const [newProject, setNewProject] = useState({
    name: '',
    category: '',
    date: '',
    description: '',
    link: '',
    order: 0,
    images: [],
  });
  const [showAddForm, setShowAddForm] = useState(false); // State to show/hide the add form

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'futiristic'));
        const projectList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log('Fetched projects:', projectList);
        setFutiristicProjects(projectList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching futiristic projects: ', error);
      }
    };

    fetchProjects();
  }, []);

  const handleChange = (id, field, value) => {
    const updatedProjects = futiristicProjects.map((project) => {
      if (project.id === id) {
        return { ...project, [field]: value };
      }
      return project;
    });
    setFutiristicProjects(updatedProjects);
  };

  const handleSave = async (id) => {
    const projectToUpdate = futiristicProjects.find((project) => project.id === id);
    const projectRef = doc(db, 'futiristic', id);
    
    try {
      await updateDoc(projectRef, {
        name: projectToUpdate.name,
        category: projectToUpdate.category,
        date: projectToUpdate.date,
        description: projectToUpdate.description,
        link: projectToUpdate.link,
        order: projectToUpdate.order,
        images: projectToUpdate.images,
      });
      console.log('Project updated successfully!');
      setEditingId(null);
    } catch (error) {
      console.error('Error updating project: ', error);
    }
  };

  const handleAddChange = (field, value) => {
    setNewProject(prev => ({ ...prev, [field]: value }));
  };

  const handleAddProject = async () => {
    try {
      await addDoc(collection(db, 'futiristic'), newProject);
      console.log('New project added successfully!');
      setShowAddForm(false);
      setNewProject({
        name: '',
        category: '',
        date: '',
        description: '',
        link: '',
        order: 0,
        images: [],
      });
      // Fetch projects again to include the new one
      const querySnapshot = await getDocs(collection(db, 'futiristic'));
      const projectList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFutiristicProjects(projectList);
    } catch (error) {
      console.error('Error adding new project: ', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null); // Reset editing state
  };

  if (loading) {
    return <div>Loading futiristic projects...</div>;
  }

  return (
    <div className="bim-projects">
      <h2>Futiristic Projects</h2>
      {futiristicProjects.length === 0 ? (
        <p>No futiristic projects available.</p>
      ) : (
        <ul>
          {futiristicProjects.map((futiristic) => (
            <li key={futiristic.id} className="project-item">
              {editingId === futiristic.id ? (
                <>
                  <input 
                    type="text" 
                    value={futiristic.name} 
                    onChange={(e) => handleChange(futiristic.id, 'name', e.target.value)} 
                  />
                  <p>
                    <strong>Category:</strong> 
                    <input 
                      type="text" 
                      value={futiristic.category} 
                      onChange={(e) => handleChange(futiristic.id, 'category', e.target.value)} 
                    />
                  </p>
                  <p>
                    <strong>Date:</strong> 
                    <input 
                      type="datetime-local" 
                      value={new Date(futiristic.date.seconds * 1000).toISOString().slice(0, 16)} 
                      onChange={(e) => handleChange(futiristic.id, 'date', new Date(e.target.value).getTime() / 1000)} 
                    />
                  </p>
                  <p>
                    <strong>Description:</strong> 
                    <textarea 
                      value={futiristic.description} 
                      onChange={(e) => handleChange(futiristic.id, 'description', e.target.value)} 
                    />
                  </p>
                  <div className="project-images">
                    <strong>Images:</strong>
                    <div className="images-grid">
                      {futiristic.images.map((image, index) => (
                        <img key={index} src={image} alt={`Project ${index}`} className="project-image" />
                      ))}
                    </div>
                  </div>
                  <p>
                    <strong>Link:</strong> 
                    <input 
                      type="url" 
                      value={futiristic.link} 
                      onChange={(e) => handleChange(futiristic.id, 'link', e.target.value)} 
                    />
                  </p>
                  <p>
                    <strong>Order:</strong> 
                    <input 
                      type="number" 
                      value={futiristic.order} 
                      onChange={(e) => handleChange(futiristic.id, 'order', e.target.value)} 
                    />
                  </p>
                  <div className="button-group">
                    <button className="cancel-button" onClick={handleCancel}>Cancel</button>
                    <button className="save-button" onClick={() => handleSave(futiristic.id)}>Save Changes</button>
                  </div>
                </>
              ) : (
                <>
                  <h3>{futiristic.name}</h3>
                  <p><strong>Category:</strong> {futiristic.category}</p>
                  <p><strong>Date:</strong> {new Date(futiristic.date.seconds * 1000).toLocaleDateString()}</p>
                  <p><strong>Description:</strong> {futiristic.description}</p>
                  <div className="project-images">
                    <strong>Images:</strong>
                    <div className="images-grid">
                      {futiristic.images.map((image, index) => (
                        <img key={index} src={image} alt={`Project ${index}`} className="project-image" />
                      ))}
                    </div>
                  </div>
                  <p><strong>Link:</strong> <a href={futiristic.link} target="_blank" rel="noopener noreferrer">Project Link</a></p>
                  <p><strong>Order:</strong> {futiristic.order}</p>
                  <button onClick={() => setEditingId(futiristic.id)}>Edit</button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <button onClick={() => setShowAddForm(!showAddForm)}>
        {showAddForm ? 'Cancel Adding' : 'Add New Futiristic Project'}
      </button>
      {showAddForm && (
        <div className="add-project-form">
          <h3>Add New Futiristic Project</h3>
          <input 
            type="text" 
            placeholder="Name" 
            value={newProject.name} 
            onChange={(e) => handleAddChange('name', e.target.value)} 
          />
          <input 
            type="text" 
            placeholder="Category" 
            value={newProject.category} 
            onChange={(e) => handleAddChange('category', e.target.value)} 
          />
          <input 
            type="datetime-local" 
            value={newProject.date} 
            onChange={(e) => handleAddChange('date', e.target.value)} 
          />
          <textarea 
            placeholder="Description" 
            value={newProject.description} 
            onChange={(e) => handleAddChange('description', e.target.value)} 
          />
          <input 
            type="url" 
            placeholder="Link" 
            value={newProject.link} 
            onChange={(e) => handleAddChange('link', e.target.value)} 
          />
          <input 
            type="number" 
            placeholder="Order" 
            value={newProject.order} 
            onChange={(e) => handleAddChange('order', Number(e.target.value))} 
          />
          <button onClick={handleAddProject}>Add Project</button>
        </div>
      )}
    </div>
  );
};

export default BIMProject;
